import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'

export enum OrderStatus {
  Active = 'active',
  Checkout = 'checkout',
  CheckoutFailed = 'checkout_failed',
  PaymentSent = 'payment_sent',
  Paid = 'paid',
  PartiallyRefunded = 'partially_refunded',
  FullyRefunded = 'fully_refunded',
}

export enum OrderItemType {
  AdCampaign = 'ad_campaign',
  SpotifyCampaign = 'spotify_campaign',
  TiktokCampaign = 'tiktok_campaign',
  SpotifyPrioritizedLaunch = 'spotify_prioritized_launch',
  TiktokPrioritizedLaunch = 'tiktok_prioritized_launch',
}

export enum OrderPaymentMethod {
  BalanceOnly = 'balance_only',
  PaypalBillingAgreement = 'paypal_billing_agreement',
  PaypalOneTime = 'paypal_one_time',
  StripeOneTime = 'stripe_one_time',
  StripeSetupIntent = 'stripe_setup_intent',
  StripeWallet = 'stripe_wallet',
}

export type OrderItem = {
  id: number
  type: OrderItemType
  sourceId: number
  fullPrice: number
}

export type Order = {
  id: number
  status: OrderStatus
  items: OrderItem[]
  checkoutSessionUrl: string | null
}

export type OrderDetailedItem = OrderItem & {
  appliedBalance: number
  itemDiscount: number
  payable: number
  vatAmount: number
  createdAt: string
}

export type OrderListItem = {
  id: number
  status: OrderStatus
  items: OrderDetailedItem[]
  checkoutSessionId: string | null
  paymentId: number | null
  createdAt: string
  updatedAt: string
}

export type CreateOrderResponse = {
  id: number
  status: OrderStatus
  userId: number
}

export type OrderCheckoutBreakdown = {
  totalAppliedBalance: number
  totalDiscount: number
  totalFullPrice: number
  totalNetPrice: number
  totalPayable: number
  totalTaxableAmount: number
  totalVatAmount: number
}

export type OrderItemDiscount = {
  couponCode: string
  discountAmount: number
  error?: string
  param?: number
}

export type OrderItemInfo = {
  coverUrl: string
  ownerName: string
  resourceName: string
}

export type OrderCheckoutItem = {
  id: number
  type: OrderItemType
  sourceId: number
  fullPrice: number
  appliedBalance: number
  payable: number
  itemDiscount: number
  vatAmount: number
  discounts: OrderItemDiscount[]
  info: OrderItemInfo
}

export type OrderCouponError = {
  couponCode: string
  error: string
  param?: number
}

export type OrderCheckoutInfoResponse = {
  id: number
  status: OrderStatus
  checkoutSessionUrl: string | null
  paymentMethods: OrderPaymentMethod[]
  couponErrors: OrderCouponError[]
  breakdown: OrderCheckoutBreakdown
  items: OrderCheckoutItem[]
}

export type OrderItemBreakdown = {
  id: number
  fullPrice: number
  appliedBalance: number
  payable: number
  itemDiscount: number
  vatAmount: number
}

type CreateOrderItemBody = {
  orderId: number
  type: OrderItemType
  sourceId: number
  fullPrice: number
}

type UpdateOrderItemBody = {
  orderId: number
  itemId: number
  fullPrice: number
}

type OrderCheckoutInfoBody = {
  coupons: string[]
}

type CreateOrderCheckoutBody = {
  paymentMethod: OrderPaymentMethod
  coupons: string[]
  itemBreakdowns: OrderItemBreakdown[]
  successUrl: string
  cancelUrl: string
}

type RemoveOrderItemsBody = {
  orderId: number
  itemIds: number[]
}

type AbandonCheckoutBody = {
  orderId: number
}

const artistOrderApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getCurrentOrder: build.query<Order | null, void>({
      query: () => `${APP.TIKTOK_SERVER}/artist/orders/current`,
      providesTags: ['ArtistOrder'],
    }),
    getOrders: build.query<OrderListItem[], void>({
      query: () => `${APP.TIKTOK_SERVER}/artist/orders`,
      providesTags: ['ArtistOrder'],
    }),
    createOrder: build.mutation<CreateOrderResponse, void>({
      query: () => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders`,
        method: 'POST',
      }),
      invalidatesTags: ['ArtistOrder'],
    }),
    addOrderItem: build.mutation<OrderItem, CreateOrderItemBody>({
      query: ({ orderId, ...body }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/${orderId}/items`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArtistOrder', 'ArtistOrderCheckoutInfo'],
    }),
    updateOrderItem: build.mutation<void, UpdateOrderItemBody>({
      query: ({ orderId, itemId, ...body }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/${orderId}/items/${itemId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['ArtistOrder', 'ArtistOrderCheckoutInfo'],
    }),
    removeOrderItems: build.mutation<void, RemoveOrderItemsBody>({
      query: ({ orderId, itemIds }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/${orderId}/items`,
        method: 'DELETE',
        body: { itemIds },
      }),
      invalidatesTags: ['ArtistOrder', 'ArtistOrderCheckoutInfo'],
    }),
    getOrderCheckoutInfo: build.query<
      OrderCheckoutInfoResponse | null,
      OrderCheckoutInfoBody
    >({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/current/checkout-info/validate`,
        method: 'POST',
        body,
      }),
      providesTags: ['ArtistOrderCheckoutInfo'],
    }),
    createOrderCheckout: build.mutation<Order, CreateOrderCheckoutBody>({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/current/pay`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ArtistOrder'],
    }),
    abandonCheckout: build.mutation<void, AbandonCheckoutBody>({
      query: ({ orderId }) => ({
        url: `${APP.TIKTOK_SERVER}/artist/orders/${orderId}/abandon`,
        method: 'POST',
      }),
      invalidatesTags: ['ArtistOrder', 'ArtistOrderCheckoutInfo'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetCurrentOrderQuery,
  useGetOrdersQuery,
  useLazyGetCurrentOrderQuery,
  useCreateOrderMutation,
  useAddOrderItemMutation,
  useUpdateOrderItemMutation,
  useRemoveOrderItemsMutation,
  useLazyGetOrderCheckoutInfoQuery,
  useCreateOrderCheckoutMutation,
  useAbandonCheckoutMutation,
} = artistOrderApi
